/**
 * Vendor
 */
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

/**
 * Shared
 */
import Spinner from '@shared/components/base/Spinner';
import ROUTES from '@shared/lib/constants/routes';
import PrivateRoute from './PrivateRoute';

/**
 * Pages
 */
const SportsPage = lazy(()      => import('@features/sports/pages/Sports'));
const LockScreen = lazy(()      => import('@features/virtual-sports/lock-screen/page/LockScreen'));

/**
 * Player's modules
 */
import { default as PlayerModules } from '@features/player/App';

/**
 * Shop's modules
 */
import { default as ShopModules } from '@features/shop/App';

import ClientLock from '@features/virtual-sports/ClientLock';

/**
 * Main App routes
 *
 * NOTE: Please do not add module routes here, Just simply import your modules
 * app here
 *
 * @class AppRoutes (name)
 * @return {DOM} { description_of_the_return_value }
 */
const AppRoutes = () => {
  // Event subroutes (must come before main sport routes to match more specific patterns first)
  const eventRoutes = [
    { component: SportsPage, rest: { exact: true, path: `${ROUTES.FOOTBALL}${ROUTES.EVENT}` } },
    { component: SportsPage, rest: { exact: true, path: `${ROUTES.BASEBALL}${ROUTES.EVENT}` } },
    { component: SportsPage, rest: { exact: true, path: `${ROUTES.BASKETBALL}${ROUTES.EVENT}` } },
    { component: SportsPage, rest: { exact: true, path: `${ROUTES.NBA}${ROUTES.EVENT}` } },
  ];

  const publicRoutes = [
    /* sports routes - publicly accessible */
    { component: SportsPage, rest: { exact: true, path: ROUTES.DEFAULT } },
    { component: SportsPage, rest: { exact: true, path: ROUTES.SPORTS } },
    { component: SportsPage, rest: { exact: true, path: ROUTES.UPCOMING } },
    { component: SportsPage, rest: { exact: true, path: ROUTES.FOOTBALL } },
    { component: SportsPage, rest: { exact: true, path: ROUTES.BASEBALL } },
    { component: SportsPage, rest: { exact: true, path: ROUTES.BASKETBALL } },
    { component: SportsPage, rest: { exact: true, path: ROUTES.NBA } },
    /* player routes - have their own auth */
    { component: PlayerModules, rest: { path: '/sports/player' } },
    /* shop routes - have their own auth */
    { component: ShopModules, rest: { path: '/sports/shop' } },
  ];

  return (
    <>
      <Suspense fallback={ <Spinner/> }>
        <Routes>
          {/**
            * NOTE:
            * We wrapped the whole app routes in this higher level here.
            * Reason is that ReactJS respect the HIGH-ORDER components.
            *
            * And we have route that are publicly available when browsed
            * via sportsbook-web directly, so we treat the client lock
            * as the highest order of component to lock everything if it's electron
            */}
          {/* auth routes */}
          <Route path={ROUTES.LOCK} element={<ClientLock><LockScreen /></ClientLock>} />

          {/* EVENT SUBROUTES - Must come before main sport routes */}
          {/* Specific event routes for football, baseball, basketball, nba */}
          {eventRoutes.map(({component: Component, rest}, index) => (
            <Route 
              key={`event-${index}`} 
              path={rest.path} 
              element={
                <ClientLock>
                  <Component />
                </ClientLock>
              } 
            />
          ))}

          {/* DYNAMIC SPORT EVENT ROUTES - Must come before main sport routes */}
          {/* Event subroutes for dynamic sports (e.g., /afl/league/event/123) */}
          <Route 
            path={`${ROUTES.SPORTS}/:sport/:league_slug/:event_description_slug/:event_id`} 
            element={
              <ClientLock>
                <SportsPage />
              </ClientLock>
            } 
          />


          {/* PUBLIC ROUTES WRAPPED IN CLIENT LOCK */}
          {publicRoutes.map(({component: Component, rest}, index) => {
            // Player and Shop routes should not be exact to allow nested routes
            const isNestedRoute = rest.path === '/sports/player' || rest.path === '/sports/shop';
            return (
              <Route 
                key={index} 
                path={isNestedRoute ? `${rest.path}/*` : rest.path} 
                element={
                  <ClientLock>
                    <Component />
                  </ClientLock>
                } 
              />
            );
          })}

          {/* DYNAMIC SPORT ROUTES - Catch-all for any sport slug (e.g., /afl, /formula-1, /handball) */}
          {/* Main sport routes for dynamic sports - must come after all specific routes */}
          <Route 
            path={`${ROUTES.SPORTS}/:sport`} 
            element={
              <ClientLock>
                <SportsPage />
              </ClientLock>
            } 
          />

          {/* ROUTE THAT ARE IN GENERAL SHOULD BE BELOW HERE */}

          <Route path="*" element={<Navigate to={ROUTES.DEFAULT} replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default AppRoutes;

